import React, { useState, useEffect } from 'react';
import { useAuth, UserRole, RoleLabels } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { 
    Users, 
    Shield,
    Trash2,
    Truck,
    ClipboardList,
    Home,
    Building2,
    Package,
    Clock,
    UserCheck,
    Heart,
    Radio,
    Megaphone,
    BadgeCheck,
    UserPlus,
    RefreshCw,
    Copy
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTIVE_ROLES = [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.COMMUNE, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.AGENT_COMMUNAUX, UserRole.RESERVE_SECURITE_CIVILE, UserRole.ENTREPRISE_EXTERIEUR, UserRole.ENTREPRISE_EXTERIEUR_CONTRAT, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.CHAUFFEUR_PL, UserRole.SOUTIEN_PSY];

const roleIcons = {
    [UserRole.CHEF_DISPOSITIF]: Shield,
    [UserRole.LOGISTIQUE]: Package,
    [UserRole.MOYEN]: Truck,
    [UserRole.GYMNASE_CHEF_POSTE]: ClipboardList,
    [UserRole.GYMNASE_ADMIN]: Home,
    [UserRole.COMMUNE]: Building2,
    [UserRole.SECOURISTE]: Heart,
    [UserRole.EQUIPIER]: Heart,
    [UserRole.AGENT_COMMUNAUX]: Building2,
    [UserRole.RESERVE_SECURITE_CIVILE]: Shield,
    [UserRole.ENTREPRISE_EXTERIEUR]: Truck,
    [UserRole.ENTREPRISE_EXTERIEUR_CONTRAT]: Truck,
    [UserRole.SECRETARIAT_COMMUNICATION]: Megaphone,
    [UserRole.POLICE_MUNICIPALE]: Shield,
    [UserRole.COS]: Radio,
    [UserRole.SOUTIEN_LOG_COMMUNE]: Building2,
    [UserRole.RENFORT_CARE]: Home,
    [UserRole.OPERATEUR_RADIO]: Radio,
    [UserRole.CHAUFFEUR_PL]: Truck,
    [UserRole.SOUTIEN_PSY]: Heart,
    [UserRole.PENDING]: Clock
};

const roleColors = {
    [UserRole.CHEF_DISPOSITIF]: 'bg-red-500/20 text-red-400 border-red-500/30',
    [UserRole.LOGISTIQUE]: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    [UserRole.MOYEN]: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    [UserRole.GYMNASE_CHEF_POSTE]: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    [UserRole.GYMNASE_ADMIN]: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    [UserRole.COMMUNE]: 'bg-green-500/20 text-green-400 border-green-500/30',
    [UserRole.SECOURISTE]: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    [UserRole.EQUIPIER]: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    [UserRole.AGENT_COMMUNAUX]: 'bg-lime-500/20 text-lime-400 border-lime-500/30',
    [UserRole.RESERVE_SECURITE_CIVILE]: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
    [UserRole.ENTREPRISE_EXTERIEUR]: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    [UserRole.ENTREPRISE_EXTERIEUR_CONTRAT]: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    [UserRole.SECRETARIAT_COMMUNICATION]: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    [UserRole.POLICE_MUNICIPALE]: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    [UserRole.COS]: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    [UserRole.SOUTIEN_LOG_COMMUNE]: 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30',
    [UserRole.RENFORT_CARE]: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    [UserRole.OPERATEUR_RADIO]: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    [UserRole.CHAUFFEUR_PL]: 'bg-stone-500/20 text-stone-400 border-stone-500/30',
    [UserRole.SOUTIEN_PSY]: 'bg-pink-500/20 text-pink-300 border-pink-500/30',
    [UserRole.PENDING]: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
};

const UsersPage = () => {
    const { token, user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [centers, setCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: UserRole.SECOURISTE });
    const [createdCreds, setCreatedCreds] = useState(null);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
        try {
            const [usersRes, centersRes] = await Promise.all([
                axios.get(`${API}/users`, { headers }),
                axios.get(`${API}/care-centers`, { headers })
            ]);
            setUsers(usersRes.data);
            setCenters(centersRes.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleRoleChange = async (userId, newRole) => {
        try {
            await axios.put(`${API}/users/${userId}`, { role: newRole }, { headers });
            toast.success('Role mis a jour');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleCenterChange = async (userId, centerId) => {
        try {
            await axios.put(`${API}/users/${userId}`, { assigned_center_id: centerId || null }, { headers });
            toast.success('Centre assigne');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleCanalRadioChange = async (userId, canalRadio) => {
        try {
            await axios.put(`${API}/users/${userId}`, { canal_radio: canalRadio }, { headers });
            toast.success('Canal radio mis a jour');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const generatePassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
        let pwd = '';
        for (let i = 0; i < 10; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
        setNewUser(prev => ({ ...prev, password: pwd }));
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/users`, newUser, { headers });
            setCreatedCreds({ name: newUser.name, email: newUser.email.trim().toLowerCase(), password: newUser.password });
            toast.success(`Compte cree pour ${newUser.name}`);
            setNewUser({ name: '', email: '', password: '', role: UserRole.SECOURISTE });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la creation');
        }
    };

    const copyCreds = () => {
        navigator.clipboard.writeText(`Acces CIVIS+\nEmail: ${createdCreds.email}\nMot de passe: ${createdCreds.password}`);
        toast.success('Identifiants copies');
    };

    const handleDelete = async (userId) => {
        if (userId === currentUser.id) {
            toast.error('Vous ne pouvez pas supprimer votre propre compte');
            return;
        }
        if (!window.confirm('Supprimer cet utilisateur ?')) return;
        try {
            await axios.delete(`${API}/users/${userId}`, { headers });
            toast.success('Utilisateur supprime');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const pendingUsers = users.filter(u => u.role === UserRole.PENDING);
    const activeUsers = users.filter(u => u.role !== UserRole.PENDING);

    const usersByRole = ACTIVE_ROLES.reduce((acc, role) => {
        acc[role] = users.filter(u => u.role === role).length;
        return acc;
    }, {});

    if (loading) {
        return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;
    }

    return (
        <div className="space-y-6" data-testid="users-page">
            <div className="page-header">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="page-title">Gestion des utilisateurs</h1>
                        <p className="page-subtitle">Administration des comptes et des roles</p>
                    </div>
                    <Button onClick={() => { setCreatedCreds(null); setCreateOpen(true); }} className="tactical-btn" data-testid="create-user-btn">
                        <UserPlus className="w-4 h-4 mr-2" /> Nouvel utilisateur
                    </Button>
                </div>
            </div>

            {/* Stats by Role */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {ACTIVE_ROLES.map(role => {
                    const Icon = roleIcons[role];
                    const count = usersByRole[role] || 0;
                    return (
                        <Card key={role} className={`stat-card border-l-4 ${roleColors[role].split(' ')[0].replace('bg-', 'border-l-').replace('/20', '')}`}>
                            <CardContent className="p-0">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="stat-value">{count}</p>
                                        <p className="stat-label text-xs">{RoleLabels[role]}</p>
                                    </div>
                                    <Icon className="w-6 h-6 opacity-50" />
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
                <Card className="stat-card border-l-4 border-l-orange-500">
                    <CardContent className="p-0">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="stat-value text-orange-400">{pendingUsers.length}</p>
                                <p className="stat-label text-xs">En attente</p>
                            </div>
                            <Clock className="w-6 h-6 text-orange-500/50" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pending Users */}
            {pendingUsers.length > 0 && (
                <Card className="content-card border-orange-500/30" data-testid="pending-users-section">
                    <CardHeader className="card-header">
                        <CardTitle className="card-title flex items-center gap-2">
                            <Clock className="w-5 h-5 text-orange-500" />
                            Demandes en attente ({pendingUsers.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {pendingUsers.map(u => (
                                <div key={u.id} className="flex items-center justify-between p-4 bg-orange-500/5 border border-orange-500/20 rounded-lg" data-testid={`pending-user-${u.id}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                                            <span className="text-lg font-semibold text-orange-400">{u.name.charAt(0).toUpperCase()}</span>
                                        </div>
                                        <div>
                                            <p className="font-medium text-white">{u.name}</p>
                                            <p className="text-sm text-zinc-400">{u.email}</p>
                                            <p className="text-xs text-zinc-500">Inscrit le {new Date(u.created_at).toLocaleDateString('fr-FR')}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Select onValueChange={(value) => handleRoleChange(u.id, value)}>
                                            <SelectTrigger className="w-[200px] bg-zinc-800 border-orange-500/30" data-testid={`assign-role-${u.id}`}>
                                                <SelectValue placeholder="Attribuer un role" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                                {ACTIVE_ROLES.map(role => {
                                                    const RIcon = roleIcons[role];
                                                    return (
                                                        <SelectItem key={role} value={role}>
                                                            <span className="flex items-center gap-2"><RIcon className="w-4 h-4" />{RoleLabels[role]}</span>
                                                        </SelectItem>
                                                    );
                                                })}
                                            </SelectContent>
                                        </Select>
                                        <Button size="sm" variant="ghost" onClick={() => handleDelete(u.id)} className="text-red-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-pending-${u.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Active Users Table */}
            <Card className="content-card">
                <CardHeader className="card-header">
                    <CardTitle className="card-title flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-500" />
                        Utilisateurs actifs ({activeUsers.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-zinc-400">Nom</TableHead>
                                <TableHead className="text-zinc-400 hidden sm:table-cell">Email</TableHead>
                                <TableHead className="text-zinc-400">Role</TableHead>
                                <TableHead className="text-zinc-400 hidden md:table-cell">Canal radio</TableHead>
                                <TableHead className="text-zinc-400 hidden md:table-cell">Centre assigne</TableHead>
                                <TableHead className="text-zinc-400 hidden sm:table-cell">Inscription</TableHead>
                                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {activeUsers.map((user) => {
                                const Icon = roleIcons[user.role] || Users;
                                return (
                                    <TableRow key={user.id} className="border-zinc-800" data-testid={`user-row-${user.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                                                    <span className="text-lg font-semibold text-white">{user.name.charAt(0).toUpperCase()}</span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-white">{user.name}</p>
                                                    {user.id === currentUser.id && <span className="text-xs text-blue-400">(vous)</span>}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell text-zinc-300">{user.email}</TableCell>
                                        <TableCell>
                                            <Select value={user.role} onValueChange={(value) => handleRoleChange(user.id, value)} disabled={user.id === currentUser.id}>
                                                <SelectTrigger className={`w-[180px] border ${roleColors[user.role] || roleColors[UserRole.PENDING]}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                                    {ACTIVE_ROLES.map(role => {
                                                        const RoleIcon = roleIcons[role];
                                                        return (
                                                            <SelectItem key={role} value={role}>
                                                                <span className="flex items-center gap-2"><RoleIcon className="w-4 h-4" />{RoleLabels[role]}</span>
                                                            </SelectItem>
                                                        );
                                                    })}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell">
                                            <Input
                                                defaultValue={user.canal_radio || ''}
                                                onBlur={(e) => {
                                                    const newVal = e.target.value;
                                                    if (newVal !== (user.canal_radio || '')) handleCanalRadioChange(user.id, newVal);
                                                }}
                                                className="tactical-input w-[130px] h-8 text-sm"
                                                placeholder="Non defini"
                                                data-testid={`canal-radio-${user.id}`}
                                            />
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell">
                                            {(user.role === UserRole.GYMNASE_ADMIN || user.role === UserRole.GYMNASE_CHEF_POSTE) ? (
                                                <Select value={user.assigned_center_id || 'none'} onValueChange={(value) => handleCenterChange(user.id, value === 'none' ? '' : value)}>
                                                    <SelectTrigger className="w-[180px] tactical-input">
                                                        <SelectValue placeholder="Non assigne" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                                        <SelectItem value="none">Non assigne</SelectItem>
                                                        {centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <span className="text-zinc-500 text-sm">N/A</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell text-zinc-500 font-mono text-sm">
                                            {new Date(user.created_at).toLocaleDateString('fr-FR')}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {user.id !== currentUser.id && (
                                                <Button size="sm" variant="ghost" onClick={() => handleDelete(user.id)}
                                                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {activeUsers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                                        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        Aucun utilisateur actif
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Create User Dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-md" data-testid="create-user-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-blue-400" /> Nouvel utilisateur</DialogTitle>
                        <DialogDescription>Creer directement un compte pret a l'emploi (ex: a remettre a un secouriste), sans demande d'inscription.</DialogDescription>
                    </DialogHeader>
                    {createdCreds ? (
                        <div className="space-y-4">
                            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg space-y-2" data-testid="created-creds-box">
                                <p className="text-emerald-400 font-medium flex items-center gap-2"><BadgeCheck className="w-4 h-4" /> Compte cree pour {createdCreds.name}</p>
                                <p className="text-sm text-zinc-300 font-mono">Email : {createdCreds.email}</p>
                                <p className="text-sm text-zinc-300 font-mono">Mot de passe : {createdCreds.password}</p>
                                <p className="text-xs text-zinc-500">Notez ces identifiants maintenant, le mot de passe ne sera plus affiche.</p>
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={copyCreds} variant="outline" className="flex-1" data-testid="copy-creds-btn">
                                    <Copy className="w-4 h-4 mr-2" /> Copier les identifiants
                                </Button>
                                <Button onClick={() => setCreatedCreds(null)} className="tactical-btn flex-1" data-testid="create-another-btn">
                                    <UserPlus className="w-4 h-4 mr-2" /> Creer un autre
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleCreateUser} className="space-y-4">
                            <div>
                                <Label className="form-label">Nom complet</Label>
                                <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="tactical-input" required data-testid="new-user-name" />
                            </div>
                            <div>
                                <Label className="form-label">Email</Label>
                                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="tactical-input" required data-testid="new-user-email" />
                            </div>
                            <div>
                                <Label className="form-label">Mot de passe</Label>
                                <div className="flex gap-2">
                                    <Input value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="tactical-input font-mono" minLength={6} required data-testid="new-user-password" />
                                    <Button type="button" variant="outline" onClick={generatePassword} title="Generer un mot de passe" data-testid="generate-password-btn">
                                        <RefreshCw className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                            <div>
                                <Label className="form-label">Role</Label>
                                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                                    <SelectTrigger className="tactical-input" data-testid="new-user-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                        {ACTIVE_ROLES.map(role => {
                                            const RIcon = roleIcons[role];
                                            return (
                                                <SelectItem key={role} value={role}>
                                                    <span className="flex items-center gap-2"><RIcon className="w-4 h-4" />{RoleLabels[role]}</span>
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Annuler</Button>
                                <Button type="submit" className="tactical-btn" data-testid="new-user-submit">Creer le compte</Button>
                            </div>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UsersPage;
